import { NgIf } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { ImageProcess, PreparedImage, prepareImage, underSizedMessage } from '../image-processing';
import { AVATAR_SIZES, ImageUploadShape, ImageUploadSize } from '../image-upload/image-upload.component';

export interface ImageUploadDialogResult {
    file: File | null;
    /** Processed PNG data URL; null when process is 'none'. */
    dataUrl: string | null;
}

/** Pick and process one image, then confirm with Save. The host saves on (save) and closes. */
@Component({
    selector: 'app-image-upload-dialog',
    templateUrl: './image-upload-dialog.component.html',
    standalone: true,
    imports: [NgIf],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageUploadDialogComponent implements OnChanges {
    @Input() open = false;
    @Input() heading = 'Upload image';
    @Input() icon = 'image';
    @Input() saveLabel = 'Save';
    @Input() accept = 'image/png,image/jpeg';
    @Input() maxBytes: number | null = null;
    @Input() hint: string | null = null;
    @Input() shape: ImageUploadShape = 'square';
    @Input() size: ImageUploadSize = 'lg';
    @Input() avatar = false;
    @Input() process: ImageProcess = 'none';
    @Input() targetWidth: number | null = null;
    @Input() targetHeight: number | null = null;
    @Input() warnIfSmaller = false;
    @Input() initialFile: File | null = null;
    @Input() saving = false;
    @Input() saveError: string | null = null;

    @Output() save = new EventEmitter<ImageUploadDialogResult>();
    @Output() closed = new EventEmitter<void>();

    picked: PreparedImage | null = null;
    validationError: string | null = null;

    constructor(private _cdr: ChangeDetectorRef) {}

    get shownError(): string | null {
        return this.validationError || this.saveError;
    }

    get avatarSize(): string {
        return AVATAR_SIZES[this.size] || AVATAR_SIZES.lg;
    }

    get underSizedText(): string {
        return underSizedMessage(this.targetWidth, this.targetHeight);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open'] && !this.open) {
            this.clear();
        }
        if (changes['initialFile'] && this.initialFile) {
            this.load(this.initialFile);
        }
    }

    onFiles(event: Event): void {
        this.load((event as CustomEvent<File[]>).detail?.[0]);
    }

    clear(): void {
        this.picked = null;
        this.validationError = null;
        this._cdr.markForCheck();
    }

    submit(): void {
        if (this.picked && !this.saving) {
            this.save.emit({ file: this.picked.file, dataUrl: this.picked.dataUrl });
        }
    }

    close(): void {
        if (!this.saving) {
            this.closed.emit();
        }
    }

    private async load(file: File | undefined): Promise<void> {
        if (!file) {
            return;
        }
        try {
            this.picked = await prepareImage(file, {
                accept: this.accept,
                maxBytes: this.maxBytes,
                process: this.process,
                targetWidth: this.targetWidth,
                targetHeight: this.targetHeight,
                warnIfSmaller: this.warnIfSmaller,
            });
            this.validationError = null;
        } catch (e) {
            this.picked = null;
            this.validationError = (e as Error).message;
        }
        this._cdr.markForCheck();
    }
}
